import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ImpersonationManager } from '../utils/impersonationManager';
import { useDemoMode } from './useDemoMode';

/**
 * Custom hook for managing authenticated WebSocket connections
 * Automatically includes JWT token in the WebSocket connection
 * 
 * @param {string} baseUrl - The base WebSocket URL (without token)
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoConnect - Whether to connect automatically on mount
 * @param {function} options.onMessage - Callback function when a message is received
 * @param {function} options.onOpen - Callback function when connection is opened
 * @param {function} options.onClose - Callback function when connection is closed
 * @param {function} options.onError - Callback function when an error occurs
 * @param {boolean} options.includeToken - Whether to include JWT token in connection (default: true)
 * @param {number} options.reconnectDelay - Delay before reconnecting (default: 1000ms)
 * @param {number} options.maxReconnectAttempts - Maximum reconnection attempts (default: 5)
 * @param {boolean} options.enableReconnect - Enable automatic reconnection (default: true)
 * @returns {Object} WebSocket connection state and methods
 */
const useAuthenticatedWebSocket = (baseUrl, options = {}) => {
  const {
    autoConnect = true,
    onMessage = () => { },
    onOpen = () => { },
    onClose = () => { },
    onError = () => { },
    includeToken = true,
    reconnectDelay = 1000,
    maxReconnectAttempts = 5,
    enableReconnect = true,
    ensureSandboxReady = false,
    sandboxStatus = null,
  } = options;

  const { token, getAccessToken, isAuthenticated, registerWebSocketDisconnectCallback } = useAuth();
  const { isDemoMode, demoAccessKey } = useDemoMode();

  // State management
  const [connectionState, setConnectionState] = useState({
    isConnected: false,
    isConnecting: false,
    error: null,
    lastMessage: null,
    reconnectAttempts: 0,
  });

  // Refs for stable references
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const urlRef = useRef(baseUrl);
  const connectPromiseRef = useRef(null);

  // Store callbacks in refs to avoid unnecessary reconnections
  const callbacksRef = useRef({ onMessage, onOpen, onClose, onError });
  useEffect(() => {
    callbacksRef.current = { onMessage, onOpen, onClose, onError };
  }, [onMessage, onOpen, onClose, onError]);

  // Helper to update state safely
  const updateState = useCallback((updates) => {
    if (isMountedRef.current) {
      setConnectionState(prev => ({ ...prev, ...updates }));
    }
  }, []);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  // Build WebSocket URL with authentication
  const buildAuthenticatedUrl = useCallback(async (url) => {
    if (!includeToken || !url) {
      return url;
    }

    // Parse existing URL to handle query parameters properly
    const urlObj = new URL(url);

    try {
      let currentToken;
      
      if (isDemoMode && demoAccessKey) {
        console.log('Building WebSocket URL for demo mode');
        // Use demo token format
        currentToken = `DEMO-${demoAccessKey}`;
      } else {
        currentToken = await getAccessToken();
        if (!currentToken) {
          console.warn('No authentication token available for WebSocket connection');
          return url;
        }
      }

      // Add token to query parameters
      urlObj.searchParams.set('token', currentToken);

      // Check for active impersonation session (using centralized function)
      const targetUserId = ImpersonationManager.getCurrentImpersonationUserId();

      // Add impersonation parameter if active
      if (targetUserId) {
        urlObj.searchParams.set('impersonate_user_id', targetUserId);
        console.log(`WebSocket connecting with impersonation: ${targetUserId}`);
      }

      const finalUrl = urlObj.toString();
      console.log('WebSocket URL with auth:', finalUrl.replace(currentToken, 'TOKEN_HIDDEN'));

      return finalUrl;
    } catch (err) {
      console.error('Error building authenticated URL:', err);
      return url;
    }
  }, [getAccessToken, includeToken, isDemoMode, demoAccessKey]);

  // Clean disconnect function
  const disconnect = useCallback(() => {
    console.log('Disconnecting WebSocket...');
    clearTimeouts();
    connectPromiseRef.current = null;

    if (socketRef.current) {
      // Remove event listeners before closing
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;

      if (socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING) {
        socketRef.current.close(1000, 'Client disconnect');
      }

      socketRef.current = null;
    }

    updateState({
      isConnected: false,
      isConnecting: false,
      reconnectAttempts: 0,
    });
  }, [clearTimeouts, updateState]);

  // Setup WebSocket event handlers
  const setupWebSocketHandlers = useCallback((ws, url) => {
    if (!ws) return;

    ws.onopen = (event) => {
      if (!isMountedRef.current) return;

      console.log('WebSocket connected successfully:', url.replace(/token=[^&]+/, 'token=HIDDEN'));
      clearTimeouts();
      updateState({
        isConnected: true,
        isConnecting: false,
        error: null,
        reconnectAttempts: 0,
      });
      callbacksRef.current.onOpen(event);
    };

    ws.onmessage = (event) => {
      if (!isMountedRef.current) return;

      try {
        const message = JSON.parse(event.data);
        updateState({ lastMessage: message });
        callbacksRef.current.onMessage(message, event);
      } catch (err) {
        // If parsing fails, pass raw data
        console.warn('Failed to parse WebSocket message as JSON:', err);
        updateState({ lastMessage: event.data });
        callbacksRef.current.onMessage(event.data, event);
      }
    };

    ws.onclose = (event) => {
      if (!isMountedRef.current) return;

      console.log(`WebSocket closed: code=${event.code}, reason=${event.reason || 'No reason provided'}`);
      updateState({
        isConnected: false,
        isConnecting: false,
      });
      callbacksRef.current.onClose(event);

      // Auto-reconnect logic
      const shouldReconnect = enableReconnect &&
        event.code !== 1000 && // Normal closure
        event.code !== 1001 && // Going away
        connectionState.reconnectAttempts < maxReconnectAttempts;

      if (shouldReconnect) {
        console.log('WebSocket closed due to possible authentication failure, will fetch fresh token on reconnect');

        const delay = Math.min(reconnectDelay * Math.pow(2, connectionState.reconnectAttempts), 30000);
        console.log(`Scheduling reconnect in ${delay}ms (attempt ${connectionState.reconnectAttempts + 1}/${maxReconnectAttempts})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && urlRef.current === baseUrl) {
            updateState(prev => ({
              ...prev,
              reconnectAttempts: prev.reconnectAttempts + 1
            }));
            connect().catch(err => {
              console.log('Reconnect attempt failed, will retry if attempts remain:', err.message);
            });
          }
        }, delay);
      }
    };

    ws.onerror = (event) => {
      if (!isMountedRef.current) return;

      console.log('WebSocket error occurred, will handle via reconnection logic');

      // Create detailed error info
      const errorInfo = {
        type: 'WebSocket Error',
        readyState: ws.readyState,
        readyStateText: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][ws.readyState],
        url: url.replace(/token=[^&]+/, 'token=HIDDEN'),
        timestamp: new Date().toISOString(),
      };

      console.log('Error details:', errorInfo);
      updateState({ error: errorInfo });
      callbacksRef.current.onError(errorInfo);
    };
  }, [clearTimeouts, updateState, enableReconnect, connectionState.reconnectAttempts, maxReconnectAttempts, reconnectDelay, baseUrl]);

  // Main connect function
  const connect = useCallback(async () => {
    // Prevent duplicate connections
    if (connectPromiseRef.current) {
      console.log('Connection already in progress');
      return connectPromiseRef.current;
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return Promise.resolve();
    }

    // Validation
    if (!baseUrl || !isMountedRef.current) {
      console.warn('Cannot connect: missing URL or component unmounted');
      return Promise.reject(new Error('Invalid connection parameters'));
    }

    // Check authentication if required (allow demo mode)
    if (includeToken && !isAuthenticated && !token && !isDemoMode) {
      console.log('Skipping WebSocket connection: authentication required but user not authenticated');
      updateState({
        error: {
          type: 'Authentication Error',
          message: 'User not authenticated'
        }
      });
      return Promise.reject(new Error('Authentication required'));
    }
    
    // Wait for sandbox to be ready if required
    if (ensureSandboxReady && sandboxStatus !== 'ready') {
      console.log('Waiting for sandbox to be ready before connecting WebSocket...');
      updateState({
        error: {
          type: 'Sandbox Not Ready',
          message: 'Waiting for sandbox to initialize'
        }
      });
      return Promise.reject(new Error('Sandbox not ready'));
    }

    // Create connection promise
    connectPromiseRef.current = (async () => {
      try {
        updateState({ isConnecting: true, error: null });

        // Clean up any existing connection
        if (socketRef.current) {
          disconnect();
          // Small delay to ensure clean disconnect
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        console.log('Initiating WebSocket connection to:', baseUrl.replace(/token=[^&]+/, 'token=HIDDEN'));

        // Build authenticated URL
        const authenticatedUrl = await buildAuthenticatedUrl(baseUrl);

        // Create WebSocket with proper error handling
        let ws;
        try {
          ws = new WebSocket(authenticatedUrl);
        } catch (err) {
          console.error('Failed to create WebSocket:', err);
          throw new Error(`Failed to create WebSocket: ${err.message}`);
        }

        // Store reference immediately
        socketRef.current = ws;

        // Setup handlers
        setupWebSocketHandlers(ws, authenticatedUrl);

        // Set connection timeout
        connectionTimeoutRef.current = setTimeout(() => {
          if (ws.readyState === WebSocket.CONNECTING) {
            console.warn('WebSocket connection timeout after 10 seconds');
            ws.close();
            updateState({
              isConnecting: false,
              error: {
                type: 'Connection Timeout',
                message: 'Failed to connect within 10 seconds'
              }
            });
          }
        }, 10000);

        // Wait for connection to open or fail
        return new Promise((resolve, reject) => {
          const originalOnOpen = ws.onopen;
          const originalOnError = ws.onerror;

          ws.onopen = (event) => {
            originalOnOpen?.(event);
            resolve();
          };

          ws.onerror = (event) => {
            // Don't reject here - let the reconnection logic handle it
            console.log('WebSocket connection failed, will be handled by reconnection logic');
            originalOnError?.(event);
            // Resolve instead of reject to prevent uncaught errors
            resolve();
          };
        });

      } catch (err) {
        console.error('Connection error:', err);
        updateState({
          isConnecting: false,
          error: {
            type: 'Connection Error',
            message: err.message
          }
        });
        throw err;
      } finally {
        connectPromiseRef.current = null;
      }
    })();

    return connectPromiseRef.current;
  }, [baseUrl, buildAuthenticatedUrl, isAuthenticated, token, includeToken, setupWebSocketHandlers, disconnect, updateState, ensureSandboxReady, sandboxStatus, isDemoMode]);

  // Send message function
  const sendMessage = useCallback((data) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send message: WebSocket not connected');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      socketRef.current.send(message);
      return true;
    } catch (err) {
      console.error('Error sending WebSocket message:', err);
      return false;
    }
  }, []);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    console.log('Manual reconnect requested');
    updateState({ reconnectAttempts: 0 });
    disconnect();

    // Small delay to ensure clean disconnect
    setTimeout(() => {
      if (isMountedRef.current) {
        connect().catch(err => {
          console.log('Manual reconnect failed, will retry via reconnection logic:', err.message);
        });
      }
    }, 100);
  }, [connect, disconnect, updateState]);

  // Test connection without authentication
  const testConnection = useCallback(async () => {
    console.log('Testing WebSocket connection to:', baseUrl);

    try {
      const testWs = new WebSocket(baseUrl);

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          testWs.close();
          reject(new Error('Connection test timeout'));
        }, 5000);

        testWs.onopen = () => {
          clearTimeout(timeout);
          console.log('Test connection successful');
          testWs.close();
          resolve(true);
        };

        testWs.onerror = (error) => {
          clearTimeout(timeout);
          console.error('Test connection failed');
          reject(new Error('Test connection failed'));
        };

        testWs.onclose = (event) => {
          clearTimeout(timeout);
          if (event.code !== 1000) {
            reject(new Error(`Connection closed with code: ${event.code}`));
          }
        };
      });
    } catch (err) {
      console.error('Test connection error:', err);
      throw err;
    }
  }, [baseUrl]);

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track URL changes
  useEffect(() => {
    urlRef.current = baseUrl;
  }, [baseUrl]);

  // Handle authentication state changes and token refresh
  useEffect(() => {
    if (!includeToken || !socketRef.current) return;

    // If we have a connection and auth state changed significantly, we may need to reconnect
    // This handles cases where the token might have been refreshed while connected
    if (socketRef.current && connectionState.isConnected) {
      console.log('Authentication state changed, connection may need token refresh');
    }
  }, [isAuthenticated, includeToken, connectionState.isConnected]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && baseUrl && !socketRef.current && !connectionState.isConnecting) {
      connect().catch(err => {
        console.log('Auto-connect failed, will retry via reconnection logic:', err.message);
      });
    }
  }, [autoConnect, baseUrl, connectionState.isConnecting]); // Minimal deps to prevent loops

  // Register disconnect callback with AuthContext for impersonation changes
  useEffect(() => {
    if (registerWebSocketDisconnectCallback) {
      const unregister = registerWebSocketDisconnectCallback(disconnect);
      return unregister;
    }
  }, [registerWebSocketDisconnectCallback, disconnect]);

  // Effect to handle connection/reconnection
  useEffect(() => {
    if (autoConnect && !socketRef.current) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Effect to monitor sandbox status and auto-retry connection when ready
  useEffect(() => {
    // Only attempt reconnection if we're waiting for sandbox and it's now ready
    if (ensureSandboxReady && 
        sandboxStatus === 'ready' && 
        !socketRef.current) {
      console.log('Sandbox is ready, checking WebSocket connection state...');
      // Small timeout to avoid race conditions with other effects
      setTimeout(() => {
        if (!socketRef.current && !connectionState.isConnecting) {
          console.log('Attempting WebSocket connection now that sandbox is ready');
          connect();
        }
      }, 100);
    }
  }, [sandboxStatus, ensureSandboxReady, connect, connectionState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
      disconnect();
    };
  }, [clearTimeouts, disconnect]);

  return {
    // Connection state
    isConnected: connectionState.isConnected,
    isConnecting: connectionState.isConnecting,
    error: connectionState.error,
    lastMessage: connectionState.lastMessage,
    reconnectAttempts: connectionState.reconnectAttempts,

    // Methods
    sendMessage,
    connect,
    disconnect,
    reconnect,
    testConnection,

    // Auth state
    isAuthenticated,
  };
};

export default useAuthenticatedWebSocket;